import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import MoreVertical from 'lucide-react/dist/esm/icons/more-vertical';
import Button from '~/modules/components/global/button';
import { Button as ShadcnButton } from '~/components/ui/button';
import Icon from '~/modules/components/global/icon';
import EmptyState from '~/modules/components/global/empty-state';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import {
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '~/components/ui/table';
import {
	DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '~/components/ui/dropdown-menu';
import SelectMenu from '~/modules/components/global/select';
import confirm from '~/modules/components/global/confirm';
import ToastService from '~/modules/services/toast-service';
import WebhookEndpointService from '~/modules/services/webhook-endpoint-service';
import WebhookSecretRevealModal from './webhook-secret-reveal-modal';
import WebhookDeliveriesModal from './webhook-deliveries-modal';
import { captureError } from '../../../pixwel/exception-handler.factory';

const QUERY_KEY = ['webhookEndpoints'];
const EVENT_TYPES_KEY = ['webhookEventTypes'];

const ALL_EVENTS_OPTION = { label: 'All events (*)', value: '*' };

const isValidHttpsUrl = (value) => {
	try {
		return new URL(value).protocol === 'https:';
	}
	catch{
		return false;
	}
};

const formatDate = (timestamp) => {
	if(!timestamp) {
		return '—';
	}
	return new Date(timestamp * 1000).toLocaleString();
};

function StatusPill({ enabled }) {
	return (
		<span
			className={
				enabled
					? 'rounded-xs bg-green-500/15 px-2 py-0.5 text-xs font-medium text-green-400'
					: 'rounded-xs bg-red-500/15 px-2 py-0.5 text-xs font-medium text-red-400'
			}
		>
			{enabled ? 'enabled' : 'disabled'}
		</span>
	);
}

StatusPill.propTypes = {
	enabled: PropTypes.bool
};

const Webhooks = () => {
	const queryClient = useQueryClient();

	const [url, setUrl] = useState('');
	const [description, setDescription] = useState('');
	const [selectedEvents, setSelectedEvents] = useState(['*']);
	const [revealedSecret, setRevealedSecret] = useState(null);
	const [deliveriesEndpoint, setDeliveriesEndpoint] = useState(null);

	const { data: endpoints = [], isLoading } = useQuery(
		QUERY_KEY,
		() => WebhookEndpointService.list(),
		{ refetchOnWindowFocus: false }
	);

	const { data: eventTypes = [] } = useQuery(
		EVENT_TYPES_KEY,
		() => WebhookEndpointService.eventTypes(),
		{ refetchOnWindowFocus: false, staleTime: Infinity }
	);

	const eventOptions = [
		ALL_EVENTS_OPTION,
		...eventTypes.map((name) => ({ label: name, value: name }))
	];

	const { mutateAsync: createEndpoint, isLoading: isCreating } = useMutation(
		(payload) => WebhookEndpointService.create(payload),
		{
			onSuccess: (endpoint) => {
				queryClient.invalidateQueries(QUERY_KEY);
				setRevealedSecret(endpoint.secret);
				setUrl('');
				setDescription('');
				setSelectedEvents(['*']);
			},
			onError: (err) => {
				ToastService.create('error', err?.response?.data?.error || err.message || 'Failed to create endpoint');
				captureError(err, `Error creating webhook endpoint: ${err.message}`);
			}
		}
	);

	const { mutateAsync: removeEndpoint } = useMutation(
		(id) => WebhookEndpointService.remove(id),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(QUERY_KEY);
				ToastService.create('success', 'Webhook endpoint deleted');
			},
			onError: (err) => {
				ToastService.create('error', err?.response?.data?.error || 'Failed to delete endpoint');
				captureError(err, `Error deleting webhook endpoint: ${err.message}`);
			}
		}
	);

	const { mutateAsync: toggleStatus } = useMutation(
		({ id, status }) => WebhookEndpointService.update(id, { status }),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(QUERY_KEY);
			},
			onError: (err) => {
				ToastService.create('error', err?.response?.data?.error || 'Failed to update endpoint');
				captureError(err, `Error updating webhook endpoint: ${err.message}`);
			}
		}
	);

	const { mutateAsync: rollSecret } = useMutation(
		(id) => WebhookEndpointService.rollSecret(id),
		{
			onSuccess: (endpoint) => {
				queryClient.invalidateQueries(QUERY_KEY);
				setRevealedSecret(endpoint.secret);
			},
			onError: (err) => {
				ToastService.create('error', err?.response?.data?.error || 'Failed to roll secret');
				captureError(err, `Error rolling webhook secret: ${err.message}`);
			}
		}
	);

	const trimmedUrl = url.trim();
	const urlError = trimmedUrl && !isValidHttpsUrl(trimmedUrl)
		? 'Enter a valid https:// URL'
		: null;

	const handleCreate = async() => {
		if(!trimmedUrl || urlError) {
			return;
		}
		try {
			await createEndpoint({
				url: trimmedUrl,
				enabledEvents: selectedEvents.length ? selectedEvents : ['*'],
				description: description.trim()
			});
		}
		catch{
			// Already reported via the mutation's onError (toast + captureError) —
			// caught here only so a rejected create doesn't surface as an
			// unhandled promise rejection.
		}
	};

	const handleDelete = async(row) => {
		const ok = await confirm({
			title: 'Delete webhook endpoint',
			content: `Delete "${row.url}"? Deliveries to this endpoint will stop immediately.`,
			confirmText: 'Delete',
			confirmVariant: 'danger',
			cancelText: 'Cancel',
			cancelVariant: 'secondary'
		});
		if(!ok) {
			return;
		}
		try {
			await removeEndpoint(row._id);
		}
		catch{
			// onError already reported this; swallow so it isn't unhandled.
		}
	};

	const handleRollSecret = async(row) => {
		const ok = await confirm({
			title: 'Roll signing secret',
			content: 'Generate a new signing secret? The current secret will stop working immediately.',
			confirmText: 'Roll secret',
			confirmVariant: 'danger',
			cancelText: 'Cancel',
			cancelVariant: 'secondary'
		});
		if(!ok) {
			return;
		}
		try {
			await rollSecret(row._id);
		}
		catch{
			// onError already reported this; swallow so it isn't unhandled.
		}
	};

	const handleToggleStatus = async(endpoint) => {
		try {
			await toggleStatus({
				id: endpoint._id,
				status: endpoint.status === 'enabled' ? 'disabled' : 'enabled'
			});
		}
		catch{
			// onError already reported this; swallow so it isn't unhandled.
		}
	};

	return (
		<div data-testid="preferences-webhooks">
			<div className="mb-8">
				<h3 className="text-base font-semibold mb-1">Create webhook endpoint</h3>
				<p className="text-sm text-muted-foreground mb-4">
					Pixwel will POST a signed JSON payload to this URL when events you can see occur. You only
					receive events for resources your account has access to. The signing secret is shown once,
					when the endpoint is created.
				</p>
				<div className="flex flex-col gap-3">
					<div className="flex flex-col gap-1.5">
						<Label>Events</Label>
						<SelectMenu
							isMulti
							options={eventOptions}
							value={eventOptions.filter((option) => selectedEvents.includes(option.value))}
							placeholder="All events"
							onChange={(selected) => setSelectedEvents(selected?.map((option) => option.value) ?? [])}
							data-testid="webhook-events-select"
						/>
					</div>
					<div className="flex gap-3 items-end flex-wrap">
						<div className="flex flex-col gap-1.5 flex-1 min-w-[16rem]">
							<Label htmlFor="webhook-url-input">Endpoint URL</Label>
							<Input
								id="webhook-url-input"
								placeholder="https://example.com/pixwel/webhooks"
								value={url}
								onChange={(e) => setUrl(e.target.value)}
								aria-invalid={!!urlError}
								aria-describedby={urlError ? 'webhook-url-error' : undefined}
								className={urlError ? 'border-destructive focus-visible:ring-destructive' : undefined}
								data-testid="webhook-url-input"
							/>
							{urlError && (
								<p id="webhook-url-error" className="text-xs text-destructive" data-testid="webhook-url-error">
									{urlError}
								</p>
							)}
						</div>
						<div className="flex flex-col gap-1.5 flex-1 min-w-[14rem]">
							<Label htmlFor="webhook-description-input">Description (optional)</Label>
							<Input
								id="webhook-description-input"
								placeholder="e.g. production integration"
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								data-testid="webhook-description-input"
							/>
						</div>
						<Button
							onClick={handleCreate}
							loading={isCreating}
							disabled={!trimmedUrl || !!urlError || isCreating}
							data-testid="webhook-create-button"
						>
							Create endpoint
						</Button>
					</div>
				</div>
			</div>

			<div>
				<h3 className="text-base font-semibold mb-3">Endpoints</h3>
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>URL</TableHead>
							<TableHead>Events</TableHead>
							<TableHead>Status</TableHead>
							<TableHead>Last delivery</TableHead>
							<TableHead></TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{isLoading
							? (
								<TableRow>
									<TableCell colSpan={5} className="text-center text-muted-foreground">Loading…</TableCell>
								</TableRow>
							)
							: endpoints.length === 0
								? (
									<TableRow>
										<TableCell colSpan={5}>
											<EmptyState message="No webhook endpoints" />
										</TableCell>
									</TableRow>
								)
								: (
									endpoints.map((endpoint) => (
										<TableRow key={endpoint._id}>
											<TableCell className="max-w-[16rem] truncate font-medium">{endpoint.url}</TableCell>
											<TableCell>
												{(endpoint.enabledEvents || []).includes('*')
													? 'all'
													: `${(endpoint.enabledEvents || []).length} selected`}
											</TableCell>
											<TableCell><StatusPill enabled={endpoint.status === 'enabled'} /></TableCell>
											<TableCell className="text-muted-foreground text-sm">
												{formatDate(endpoint.lastSuccessAt)}
											</TableCell>
											<TableCell className="text-right">
												<div className="flex items-center justify-end gap-2">
													<Button
														variant="secondary"
														data-testid="webhook-deliveries-button"
														onClick={() => setDeliveriesEndpoint(endpoint)}
													>
														Deliveries
													</Button>
													<DropdownMenu>
														<DropdownMenuTrigger asChild>
															<ShadcnButton
																variant="ghost"
																size="icon"
																aria-label="Endpoint actions"
																data-testid="webhook-actions-menu"
															>
																<MoreVertical className="h-4 w-4" />
															</ShadcnButton>
														</DropdownMenuTrigger>
														<DropdownMenuContent align="end">
															<DropdownMenuItem
																data-testid="webhook-toggle-button"
																onSelect={() => handleToggleStatus(endpoint)}
															>
																{endpoint.status === 'enabled' ? 'Disable' : 'Enable'}
															</DropdownMenuItem>
															<DropdownMenuItem
																data-testid="webhook-roll-secret-button"
																onSelect={() => handleRollSecret(endpoint)}
															>
																Roll secret
															</DropdownMenuItem>
															<DropdownMenuItem
																data-testid="webhook-delete-button"
																className="text-destructive focus:text-destructive"
																onSelect={() => handleDelete(endpoint)}
															>
																<Icon use="trash" color="currentColor" size={0.875} /> Delete
															</DropdownMenuItem>
														</DropdownMenuContent>
													</DropdownMenu>
												</div>
											</TableCell>
										</TableRow>
									))
								)}
					</TableBody>
				</Table>
			</div>

			<WebhookSecretRevealModal
				secret={revealedSecret}
				onClose={() => setRevealedSecret(null)}
			/>
			<WebhookDeliveriesModal
				endpoint={deliveriesEndpoint}
				onClose={() => setDeliveriesEndpoint(null)}
			/>
		</div>
	);
};

export default Webhooks;
